/**
 * Turning a record's related ROWS into map PINS, using the governed recipe.
 *
 * ── WHY THIS IS A PURE FUNCTION IN ITS OWN FILE ────────────────────────────
 * Everything decided here is a rule somebody can get wrong quietly: which rows
 * are placeable, what a pin is CALLED, what its type label is, and — the one
 * that matters most — what happens to the rows that cannot be placed. None of
 * that needs React, a query or a canvas, and testing it through any of the three
 * would mean testing it through jsdom's missing layout.
 *
 * ── NO RELATIONSHIP KEY APPEARS HERE ───────────────────────────────────────
 * The retired CRM panel knew `located_at` by name and read `core.address` with
 * its own query. This function knows only:
 *
 *   · the far ENTITY of each group, and the recipe the SERVER resolved for it;
 *   · the rows the ordinary relationship read already returned;
 *   · two resolvers the host supplies — the canonical title and the governed
 *     type label.
 *
 * So an entity that gains coordinates becomes map-capable with no console
 * change, which is the whole difference between a lens and an exception.
 *
 * ── AND NOTHING IS INVENTED ────────────────────────────────────────────────
 * A title the caller may not resolve is not replaced by a uuid or by a guessed
 * column: `core.titles_of` is caller-RLS and answering nothing is a legitimate
 * outcome, so the pin says what the list rows say in that case. A type the host
 * could not resolve produces NO type line at all rather than the raw stored
 * code — an honest gap beats `billing` reaching a customer's screen.
 */
import type { Row } from '@solum/sdk'
import type { MapPin, MapUnplaceableRecord } from '@solum/ui'
import { asCoordinate, isPlaceable } from '@solum/ui'
import type { MapRecipe } from '@solum/experience'

/** One relationship group, with whatever the server said about placing it. */
export interface MapPinSource {
  /** The slot id — the group's identity on this surface. */
  slotId: string
  /** What the person sees this group called. */
  groupLabel: string
  /** The far entity. */
  targetEntity: string
  /** The governed recipe for that entity, or null when it declares none. */
  recipe: MapRecipe | null
  rows: readonly { record: Row; edge: Row | null }[]
}

export interface MapPinResolvers {
  /** The canonical record title (`core.titles_of`), caller-RLS. */
  title: (type: string, id: unknown, fallback?: string) => string
  /** The canonical subtitle, when the entity declares one. */
  subtitle: (type: string, id: unknown) => string | null
  /**
   * The governed label for a relationship's own type/purpose, from METADATA.
   *
   * Returns undefined when the host cannot resolve one — which is a real state,
   * not an error: the edge may carry no attribute, the module may register no
   * catalogue for it, or the caller may not be able to read one.
   */
  typeLabel: (edge: Row | null, targetEntity: string) => string | undefined
  /** Whether the host has a destination for this record at all. */
  openable?: (targetEntity: string, id: string) => boolean
}

export interface MapPinSet {
  pins: MapPin[]
  unplaceable: MapUnplaceableRecord[]
  /** Groups with no recipe at all — the workspace still draws them as sections. */
  unmappableGroups: string[]
}

/**
 * Build the pins, and the list of everything that could not become one.
 *
 * The two outputs are the contract. A row is in exactly one of them, always, and
 * the count of both together equals the rows that came in — which is the
 * property `mapPins.test.ts` asserts first, because every other guarantee this
 * component makes rests on a record never falling between them.
 */
export function buildMapPins(
  sources: readonly MapPinSource[],
  resolvers: MapPinResolvers,
): MapPinSet {
  const pins: MapPin[] = []
  const unplaceable: MapUnplaceableRecord[] = []
  const unmappableGroups: string[] = []
  const multiGroup = sources.length > 1

  for (const source of sources) {
    if (!source.recipe) {
      // NOT an error, and NOT a reason to drop the group: an organisation's
      // people and opportunities have no coordinates and never will. They stay
      // in their ordinary sections beneath the canvas.
      if (source.rows.length > 0) unmappableGroups.push(source.slotId)
      continue
    }
    const { lat: latKey, lng: lngKey, title: titleKey, subtitle: subtitleKey } = source.recipe

    for (const { record, edge } of source.rows) {
      const id = String(record.id ?? '')
      if (!id) continue
      const key = `${source.slotId}:${id}`
      const typeLabel = resolvers.typeLabel(edge, source.targetEntity)
      // The canonical title first, then the recipe's own column if it named one.
      // In that order deliberately: `core.titles_of` is the platform's answer to
      // "what is this record called", and a recipe naming a column is the
      // EXCEPTION for an entity whose canonical title is not what a pin should
      // say — so it must not silently win over the canonical one everywhere.
      const canonical = resolvers.title(source.targetEntity, id, '')
      const fromRecipe = titleKey ? stringOr(record[titleKey]) : ''
      const title = canonical || fromRecipe || 'A record you cannot open'
      const subtitle = (subtitleKey ? stringOr(record[subtitleKey]) : '')
        || resolvers.subtitle(source.targetEntity, id) || undefined
      const openable = resolvers.openable?.(source.targetEntity, id) ?? true

      const rawLat = record[latKey]
      const rawLng = record[lngKey]
      const shared = {
        key, entity: source.targetEntity, id, title,
        ...(typeLabel ? { typeLabel } : {}),
        ...(multiGroup ? { groupLabel: source.groupLabel } : {}),
        openable,
      }

      // `isPlaceable` is the ONE place this question is answered — the same
      // function the lens itself uses. Re-deriving the test here is how a host
      // and its renderer come to disagree about which records disappear, and the
      // first draft of this file did exactly that: it hand-wrote the range check
      // beside `asCoordinate`, which is two copies of one rule.
      if (!isPlaceable(rawLat, rawLng)) {
        unplaceable.push({ ...shared, reason: reasonFor(rawLat, rawLng) })
        continue
      }
      pins.push({
        ...shared, ...(subtitle ? { subtitle } : {}),
        lat: asCoordinate(rawLat)!, lng: asCoordinate(rawLng)!,
      })
    }
  }

  return { pins, unplaceable, unmappableGroups }
}

const stringOr = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : value == null ? '' : String(value)

/**
 * Why this row is not on the canvas, in words a person can act on.
 *
 * MISSING and INVALID are different sentences because they need different
 * actions: one is "nobody has located this yet", the other is "what is stored
 * here is not a coordinate" — and a single message for both would send somebody
 * looking for a geocoder when the real problem is a bad import.
 */
function reasonFor(lat: unknown, lng: unknown): string {
  const missing = (v: unknown) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
  if (missing(lat) && missing(lng)) return 'No coordinates yet'
  if (missing(lat) || missing(lng)) return 'Only one coordinate is set'
  return 'The stored coordinates are not valid'
}
